// <s-video-carousel> — auto-advancing row of videos that stops for good once a buyer plays one.
// Commands on descendant buttons: --toggle (pause/play autoplay), --prev, --next.
// Required markup: [data-track] scroll-snap track whose children are slides; [data-status] polite live region.
class SVideoCarousel extends HTMLElement {
  #ac;
  #timer = null;
  #raf = 0;
  #track;
  #toggle;
  #status;
  #paused = { hover: false, focus: false, hidden: false, user: false, motion: false, mobile: false };

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-track]');
    this.#toggle = this.querySelector('[data-toggle]');
    this.#status = this.querySelector('[data-status]');
    if (!this.#track) return;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#paused.motion = motionQuery.matches;
    this.#paused.user = !this.hasAttribute('autoplay');
    motionQuery.addEventListener('change', (e) => { this.#paused.motion = e.matches; this.#sync(); }, opts);

    // Optionally hold the row still on small screens so the start slide stays centered until a shopper browses.
    if (this.hasAttribute('data-static-mobile')) {
      const mobileQuery = window.matchMedia('(max-width: 767px)');
      this.#paused.mobile = mobileQuery.matches;
      mobileQuery.addEventListener('change', (e) => { this.#paused.mobile = e.matches; this.#sync(); }, opts);
    }

    this.addEventListener('pointerenter', () => { this.#paused.hover = true; this.#sync(); }, opts);
    this.addEventListener('pointerleave', () => { this.#paused.hover = false; this.#sync(); }, opts);
    this.addEventListener('focusin', () => { this.#paused.focus = true; this.#sync(); }, opts);
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) { this.#paused.focus = false; this.#sync(); }
    }, opts);
    document.addEventListener('visibilitychange', () => { this.#paused.hidden = document.hidden; this.#sync(); }, opts);

    // Playing any video is a sticky stop: hover-out or tab-out must never restart the row under a playing video.
    this.#track.addEventListener('play', () => this.#stop(), { ...opts, capture: true });
    this.#track.addEventListener('s-video:play', () => this.#stop(), opts);
    this.#track.addEventListener('click', (e) => {
      if (e.target.closest('[data-play], video')) this.#stop();
    }, opts);

    this.addEventListener('command', (e) => {
      if (e.command === '--toggle') this.#paused.user ? this.#resume() : this.#stop();
      if (e.command === '--next') this.#step(1, false);
      if (e.command === '--prev') this.#step(-1, false);
    }, opts);

    // Open with the chosen slide centered in the row, then re-center if the layout changes size before anyone interacts.
    this.#centerStart();
    requestAnimationFrame(() => this.#centerStart());
    let touched = false;
    const markTouched = () => { touched = true; };
    this.#track.addEventListener('pointerdown', markTouched, opts);
    this.#track.addEventListener('wheel', markTouched, { ...opts, passive: true });
    this.addEventListener('command', markTouched, opts);
    window.addEventListener('load', () => { if (!touched) this.#centerStart(); }, opts);
    window.addEventListener('resize', () => { if (!touched) this.#centerStart(); }, opts);

    this.#sync();
  }

  #centerStart() {
    const index = Math.max(1, Number(this.getAttribute('start')) || 1) - 1;
    const slide = this.#track.children[index];
    if (!slide) return;
    const trackRect = this.#track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const offset = (slideRect.left + slideRect.width / 2) - (trackRect.left + trackRect.width / 2);
    this.#track.scrollBy({ left: offset, behavior: 'auto' });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearInterval(this.#timer);
    cancelAnimationFrame(this.#raf);
  }

  get #interval() {
    const seconds = Number(this.getAttribute('speed')) || 3;
    return Math.max(1.5, seconds) * 1000;
  }

  #isRunning() {
    return !Object.values(this.#paused).some(Boolean);
  }

  #sync() {
    clearInterval(this.#timer);
    this.#timer = null;
    if (this.#isRunning()) this.#timer = setInterval(() => this.#step(1, true), this.#interval);
    this.#track.setAttribute('aria-live', this.#isRunning() ? 'off' : 'polite');
    if (this.#toggle) {
      const running = !this.#paused.user;
      this.#toggle.setAttribute('aria-label', running ? this.#toggle.dataset.pauseLabel : this.#toggle.dataset.playLabel);
      this.#toggle.dataset.state = running ? 'playing' : 'paused';
    }
  }

  #stop() {
    if (this.#paused.user) return;
    this.#paused.user = true;
    this.#sync();
    this.#announce(this.dataset.pausedMessage);
  }

  #resume() {
    this.#paused.user = false;
    this.#sync();
    this.#announce(this.dataset.playingMessage);
  }

  #step(direction, loop) {
    const track = this.#track;
    const slide = track.firstElementChild;
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = slide.getBoundingClientRect().width + gap;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    const position = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth;
    const behavior = this.#paused.motion ? 'auto' : 'smooth';

    if (loop && direction > 0 && position >= max - 2) {
      track.scrollTo({ left: 0, behavior });
    } else {
      track.scrollBy({ left: distance * direction * rtl, behavior });
    }
    if (!loop) this.#sync();
  }

  #announce(text) {
    if (!this.#status || !text) return;
    cancelAnimationFrame(this.#raf);
    this.#status.textContent = '';
    this.#raf = requestAnimationFrame(() => { this.#status.textContent = text; });
  }
}

if (!customElements.get('s-video-carousel')) customElements.define('s-video-carousel', SVideoCarousel);
